import { ref } from 'vue';
import { LegalProcessesApi } from 'dashboard/api/legalProcesses';
import { LegalClientsApi } from 'dashboard/api/legalClients';
import LegalCasesApi from 'dashboard/api/legalCases';
import { legalActivitiesAPI } from 'dashboard/api/legalActivities';
import ConversationApi from 'dashboard/api/conversations';
import { ranquear } from './buscaRanking';

/**
 * Segundo estágio dos comandos com barra: busca a entidade e leva até ela.
 *
 * TRÊS COISAS QUE ESTE ARQUIVO RESOLVE, E POR QUE
 *
 * 1. Cada entidade tem endpoint e formato PRÓPRIOS (o de processo devolve
 *    `title`/`number_cnj`, o de cliente aninha tudo em `contact`, o de agenda
 *    chama o título de `description`). O adaptador normaliza para
 *    `{ id, titulo, subtitulo, rota }` — sem isso a diferença vaza para o
 *    template e cada linha do menu precisaria de um `v-if`.
 *
 * 2. O adaptador também decide o DESTINO, e não só o resultado. Caso é o
 *    exemplo: não existe rota `legal_case_show`, então caso abre o processo
 *    principal — exatamente o que a tela de Casos já faz ao clicar numa linha.
 *    Deixar a decisão junto da busca evita um `switch` de rota no componente.
 *
 * 3. Corrida de resposta. Digitar "sou" → "souz" → "souza" dispara três buscas;
 *    se a primeira voltar por último, o menu mostra o resultado de "sou" com
 *    "souza" escrito na tela. O contador `geracao` descarta resposta velha.
 *    É o bug clássico de busca-enquanto-digita, e ele não aparece em teste
 *    manual: só com rede ruim, que é justamente onde o usuário está.
 */

// Mesmo intervalo da busca global do jurídico (`legal/GlobalSearch.vue`).
// Alinhar não é estética: a pessoa aprende UM tempo de resposta no produto.
export const DEBOUNCE_MS = 300;

// Abaixo disso o resultado é grande demais para significar algo — e o backend
// já devolve vazio para menos de 2 caracteres em `global_search`.
export const MIN_CARACTERES = 2;

// Quantas linhas o menu mostra.
const LIMITE = 6;

// Quantas o backend traz. Maior que o que se mostra DE PROPÓSITO: o backend
// busca com `ILIKE %termo%` e não sabe ordenar por relevância, então pedir 6
// pode trazer 6 casadas no meio da palavra e deixar de fora a que começa com o
// termo. Traz 25, reordena aqui (ver buscaRanking) e mostra as 6 melhores.
//
// O limite disso: com mais de 25 resultados casando no meio, a boa ainda pode
// ficar de fora. A correção definitiva é o backend casar por começo de palavra
// — está anotado no spec como o passo seguinte. 25 cobre a busca de escritório
// real sem inventar um endpoint novo agora.
const LIMITE_BUSCA = 25;

/**
 * `Channel::WebWidget` não diz nada para quem lê o menu. A parte depois do
 * `::` é o que a interface do Chatwoot já mostra em outros lugares.
 */
export const canalLegivel = channelType =>
  String(channelType ?? '')
    .split('::')
    .pop();

/**
 * Status de conversa (`open`, `resolved`, `pending`, `snoozed`) já tem tradução
 * na lista de conversas. Reusar aquela tabela é o que impede duas versões da
 * mesma coisa divergirem — sem `t`, devolve o valor cru em vez da chave.
 */
export const statusDaConversaLegivel = (status, t) => {
  if (!status) return '';
  if (!t) return String(status);
  const chave = `CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`;
  const traduzido = t(chave);
  return traduzido === chave ? String(status) : traduzido;
};

const formatarQuando = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Cada adaptador: `(termo, rota) => Promise<Array<{id,titulo,subtitulo,rota}>>`
 * onde `rota` é o `accountScopedRoute` do `useAccount`.
 */
const ADAPTADORES = {
  processo: async (termo, rota) => {
    const { data } = await LegalProcessesApi.globalSearch(termo, LIMITE_BUSCA);
    return (data?.payload?.processos || []).map(p => ({
      id: p.id,
      titulo: p.title || p.number_cnj,
      // A PASTA VEM PRIMEIRO no subtítulo. É por ela que o escritório chama o
      // processo, e sem ela na linha quem busca "261" recebe
      // "Execução · 5009667-43..." e conclui que a busca ignorou o que pediu —
      // mesmo com o processo certo na tela (Victor, 09/09/2026).
      subtitulo: [p.folder_number, p.number_cnj, p.contact_name]
        .filter(Boolean)
        .join(' · '),
      tipo: 'processo',
      // O ranking usa isto para pôr a pasta EXATA no topo: "261" casa no meio
      // de "0000261" e, sem esta dica, empata com qualquer CNJ que contenha
      // 261 no meio.
      pasta: p.folder_number,
      refId: p.id,
      rota: rota('legal_process_show', { processId: p.id }),
    }));
  },

  cliente: async (termo, rota) => {
    const { data } = await LegalClientsApi.getAll({
      q: termo,
      per_page: LIMITE_BUSCA,
    });
    return (data?.payload || []).map(c => ({
      id: c.id,
      titulo: c.contact?.name || c.trade_name || `Cliente #${c.id}`,
      subtitulo: [c.cpf_cnpj, c.contact?.phone_number]
        .filter(Boolean)
        .join(' · '),
      tipo: 'cliente',
      refId: c.id,
      rota: rota('legal_client_show', { clientId: c.id }),
    }));
  },

  caso: async (termo, rota) => {
    const { data } = await LegalCasesApi.getAll({
      q: termo,
      per_page: LIMITE_BUSCA,
    });
    return (data?.payload || []).map(c => ({
      id: c.id,
      titulo: c.name || `Pasta ${c.folder_number}`,
      subtitulo: [c.folder_number && `Pasta ${c.folder_number}`, c.contact_name]
        .filter(Boolean)
        .join(' · '),
      // Não existe tela de caso: a de Casos abre o processo principal ao
      // clicar. Caso sem processo principal cai no índice, que é para onde a
      // pessoa teria de ir de qualquer jeito. A referência guarda o DESTINO
      // final, não o caso — senão o link teria que refazer essa decisão anos
      // depois, com dados que podem ter mudado.
      tipo: c.primary_legal_process_id ? 'processo' : 'caso',
      refId: c.primary_legal_process_id || c.id,
      rota: c.primary_legal_process_id
        ? rota('legal_process_show', { processId: c.primary_legal_process_id })
        : rota('legal_cases_index'),
    }));
  },

  // Atendimento do inbox. Reusa `conversations#search`, que o backend já tem e
  // que a agenda jurídica já chama para vincular atendimento a compromisso.
  //
  // MEDIDO antes de decidir (2026-08-07, dev): a resposta custa ~1,9 KB por
  // conversa e 59 ms. Para as 25 que pedimos dá ~48 KB — gordo para um menu de
  // 6 linhas, mas é ação manual e esporádica, e um endpoint enxuto novo custaria
  // backend para economizar 40 KB. Se um dia o menu ficar lento numa base
  // grande, o número acima é o ponto de partida.
  //
  // A busca do backend já cobre nome do contato, telefone, conteúdo da mensagem
  // e o NÚMERO do atendimento (`#336`) — nada disso precisou ser escrito aqui.
  conversa: async (termo, rota, t) => {
    const { data } = await ConversationApi.search({ q: termo });
    return (data?.payload || []).map(c => ({
      id: c.id,
      titulo: c.meta?.sender?.name || `#${c.id}`,
      // Canal e status juntos porque cinco linhas com o mesmo nome de contato
      // são indistinguíveis — é o mesmo motivo do CNJ no subtítulo do processo.
      subtitulo: [
        `#${c.id}`,
        canalLegivel(c.meta?.channel),
        // Mesma tabela que a lista de conversas usa: "Abertas", não "open".
        statusDaConversaLegivel(c.status, t),
      ]
        .filter(Boolean)
        .join(' · '),
      tipo: 'conversa',
      refId: c.id,
      rota: rota('inbox_conversation', { conversation_id: c.id }),
    }));
  },

  agenda: async (termo, rota) => {
    // `limit`, e não `per_page`: a agenda não pagina (calendário e kanban
    // precisam do período inteiro), então o corte é um parâmetro opcional que
    // só quem quer as primeiras linhas manda. Sem ele vinham 16 numa base de
    // teste — numa produção real viria a agenda toda para um menu de 6 linhas.
    const { data } = await legalActivitiesAPI.list({
      q: termo,
      limit: LIMITE_BUSCA,
    });
    return (data?.payload || []).map(a => ({
      id: a.id,
      titulo: a.description || `Compromisso #${a.id}`,
      // Pasta e cliente entram no subtítulo pelo mesmo motivo do `/processo`:
      // a busca passou a achar por eles, e linha que não mostra o que casou
      // parece busca quebrada. A data continua, porque compromisso sem quando
      // não diz nada.
      subtitulo: [
        a.legal_process?.folder_number,
        a.contact?.name,
        formatarQuando(a.start_at),
      ]
        .filter(Boolean)
        .join(' · '),
      tipo: 'agenda',
      pasta: a.legal_process?.folder_number,
      refId: a.id,
      // A agenda não tem rota por atividade — o detalhe é modal. `?atividade=`
      // faz a lista abrir o modal ao montar (ver AgendaActivitiesList).
      rota: rota('appointments_agenda', {}, { atividade: String(a.id) }),
    }));
  },
};

/**
 * Destino dos comandos de AÇÃO (os sem `entidade`, que não têm o que buscar).
 * Mora junto dos adaptadores de propósito: destino de comando é um assunto só.
 *
 * @returns {object|null} rota, ou null se o comando não tiver ação conhecida
 */
export const rotaDaAcao = (comandoId, rota) => {
  if (comandoId === 'tarefa') {
    return rota('appointments_agenda', {}, { nova: '1' });
  }
  return null;
};

/**
 * Referência a uma entidade dentro de uma mensagem.
 *
 * `[jane](advup://cliente/5)` — MESMO formato da menção
 * (`[@Nome](mention://user/7/...)`), de propósito: o renderizador já sabe lidar
 * com `[rótulo](esquema://...)`, e um segundo formato seria um segundo parser
 * para manter em sincronia. A divergência entre dois parsers já custou um bug
 * neste módulo (prévia da sidebar).
 *
 * O comando NÃO navega: ele escreve isto no campo, a mensagem é enviada, e
 * quem lê clica. Pedido do Victor em 2026-08-06 — "a ideia é eu enviar isso lá
 * na conversa, e aquilo se tornar como se fosse um link pra pessoa ir lá no
 * cliente Jane". Faz mais sentido: quem digita já sabe qual é o processo; quem
 * precisa do atalho é quem lê.
 */
export const marcadorDeReferencia = (rotulo, tipo, id) =>
  `[${String(rotulo).replace(/[[\]]/g, '')}](advup://${tipo}/${id})`;

// Rota de cada tipo de referência. Usada na hora de CLICAR — o marcador guarda
// só tipo e id, e é aqui que isso vira tela.
const ROTA_POR_TIPO = {
  processo: id => ['legal_process_show', { processId: id }],
  cliente: id => ['legal_client_show', { clientId: id }],
  caso: () => ['legal_cases_index', {}],
  agenda: id => ['appointments_agenda', {}, { atividade: String(id) }],
  conversa: id => ['inbox_conversation', { conversation_id: String(id) }],
  // A ficha da publicação, pelo link de entrada da tela de Publicações
  // (`?publicacao=`, o mesmo do aviso de publicação nova). Usada pelas fontes
  // do Lumos (08/10/2026).
  publicacao: id => [
    'legal_publications_index',
    {},
    { publicacao: String(id) },
  ],
  // Fatia 4 C (20/10/2026): o que a busca do Copiloto acha numa nota de contato e num e-mail.
  contato: id => ['contacts_edit', { contactId: String(id) }],
  email: () => ['correio_index', {}],
};

export const rotaDaReferencia = (tipo, id, rota) => {
  const monta = ROTA_POR_TIPO[tipo];
  if (!monta) return null;
  const [nome, params, query] = monta(id);
  return rota(nome, params, query);
};

const INDICES = {
  processo: 'legal_processes_index',
  cliente: 'legal_clients_index',
  caso: 'legal_cases_index',
  agenda: 'appointments_agenda',
};

/**
 * A tela cheia da entidade. Serve de saída quando a busca não acha nada: em vez
 * de beco sem saída, a pessoa vai para a lista completa e procura de lá.
 *
 * @returns {object|null}
 */
export const rotaDoIndice = (comandoId, rota) =>
  INDICES[comandoId] ? rota(INDICES[comandoId]) : null;

/**
 * @param {Function} accountScopedRoute
 * @param {Function} [t] tradutor. Opcional porque só `/conversa` precisa dele
 *   (status vem cru do backend) e os specs dos outros adaptadores não passam.
 */
export function useSlashSearch(accountScopedRoute, t) {
  const resultados = ref([]);
  const carregando = ref(false);
  const buscou = ref(false);

  let temporizador = null;
  let geracao = 0;

  const limpar = () => {
    clearTimeout(temporizador);
    geracao += 1;
    resultados.value = [];
    carregando.value = false;
    buscou.value = false;
  };

  const buscar = (comandoId, termo) => {
    const adaptador = ADAPTADORES[comandoId];
    const q = String(termo ?? '').trim();
    clearTimeout(temporizador);

    if (!adaptador || q.length < MIN_CARACTERES) {
      geracao += 1;
      resultados.value = [];
      carregando.value = false;
      buscou.value = false;
      return;
    }

    geracao += 1;
    const minha = geracao;
    carregando.value = true;

    temporizador = setTimeout(async () => {
      try {
        const itens = await adaptador(q, accountScopedRoute, t);
        // Resposta de uma digitação anterior: descartar em silêncio.
        if (minha !== geracao) return;
        // Reordena ANTES de cortar: o corte tem que cair sobre a lista já
        // ranqueada, senão jogaria fora justamente o que subiu.
        resultados.value = ranquear(itens, q).slice(0, LIMITE);
      } catch {
        if (minha !== geracao) return;
        // Falha de rede devolve lista vazia, não erro na tela: o menu é um
        // atalho, não pode virar um alerta por cima do que a pessoa escreve.
        resultados.value = [];
      } finally {
        if (minha === geracao) {
          carregando.value = false;
          buscou.value = true;
        }
      }
    }, DEBOUNCE_MS);
  };

  return { resultados, carregando, buscou, buscar, limpar };
}
